import { Button, styled, TextField, LinearProgress } from "@mui/material";
import video_icon from "../../icons/video_icon.svg";
import image_icon from "../../icons/image_icon.svg";
import colors from "../../../theme/themeConstants";
import React, { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { fetchUrl } from "../../../utils/upload";
import { FileUploader } from "react-drag-drop-files";

import {
  GIF_MIME_TYPE,
  VIDEO_MIME_TYPES,
  IMAGE_MIME_TYPES,
  validateSize,
  validateMimeType
} from "utils/uploadFileValidation";

import { toast } from "react-hot-toast";
import { FileSizeError } from "components/upload/FileSizeError";
import { FileMimeTypeError } from "components/upload/FileMimeTypeError";
import { addFiles } from "utils/uploadAddFile";

interface IProps {
  setFileType: (type: string) => void;
  setFile: (file: any) => void;
  fileUrl: string;
  setFileUrl: (url: string) => void;
  setFiles: (files: any) => void;
  setFromUrl: (url: string) => void;
  setTimeLineURL: (url: string) => void;
  setTicketId: (id: any) => void;
}

const videoAcceptTypes = [...VIDEO_MIME_TYPES, GIF_MIME_TYPE];
const imageAcceptTypes = IMAGE_MIME_TYPES;

const UploadSelectGifType = (props: IProps) => {
  const {
    setFileType,
    setFile,
    setFileUrl,
    setFromUrl,
    fileUrl,
    setFiles,
    setTimeLineURL,
    setTicketId
  } = props;

  const [urlLoad, setUrlLoad] = useState(false);
  const [urlError, setUrlError] = useState("");
  const imageInputRef = useRef<any>(null);
  const videoInputRef = useRef<any>(null);

  const handleFileUrl = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const urlVal = e.target.value;
    setUrlLoad(true);
    setFileUrl(urlVal);
    setUrlError("");

    try {
      const { downloadUrl, timelineUrl, kind, id } = await fetchUrl({ url: urlVal });

      setFileUrl(downloadUrl);
      setFileType(kind);
      setFromUrl(downloadUrl);
      setTicketId(id);

      if (kind === "video") {
        setTimeLineURL(timelineUrl || "");
      }
    } catch (err) {
      setUrlError("Error uploading a file.");
    } finally {
      setUrlLoad(false);
    }
  };

  const handleButtonClick = (type: string) => {
    if (type === "video") {
      if (videoInputRef && videoInputRef.current) {
        videoInputRef.current.click();
      }
    } else if (type === "image") {
      if (imageInputRef && imageInputRef.current) {
        imageInputRef.current.click();
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, acceptTypes: string[]) => {
    const newFiles = event.target.files;
    if (newFiles === null) {
      return;
    }

    if (!validateMimeType(newFiles, acceptTypes)) {
      toast.error(FileMimeTypeError, {
        duration: 5000
      });
      return;
    }

    handleFile(newFiles);
  };

  const handleFile = async (newFiles: FileList) => {
    if (!validateSize(newFiles)) {
      toast.error(FileSizeError, {
        duration: 5000
      });
      return;
    }

    if (newFiles.length < 2) {
      const type = validateMimeType(newFiles, VIDEO_MIME_TYPES) ? "video" : "image";
      const [{ url, file }] = await addFiles([], newFiles);
      setFileUrl(url);
      setFile(file);
      setFileType(type);
    } else {
      setFileType("multiple");
      setFiles(await addFiles([], newFiles));
    }
  };

  const handleDropFileChange = (file: File) => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    if (validateMimeType(dataTransfer.files, VIDEO_MIME_TYPES)) {
      handleFile(dataTransfer.files);
      return;
    }

    if (validateMimeType(dataTransfer.files, IMAGE_MIME_TYPES)) {
      handleFile(dataTransfer.files);
      return;
    }

    toast.error(FileMimeTypeError, {
      duration: 5000
    });
  };

  return (
    <ContainerWrap>
      <FileUploader
        classes="file-drop"
        handleChange={handleDropFileChange}
        name="file"
        disabled
      >
        <Container>
          <h4>Choose file or define URL</h4>
          <Button variant="contained" fullWidth sx={{
            background: "transparent",
            border: `2px solid ${colors.purple}`,
            minHeight: "3rem",
            fontSize: "16px",
            fontWeight: "500",
            borderRadius: "38px",
            marginTop: "24px",
            display: "flex",
            alighItems: "center",
            gap: "8px",
            "&:hover": {
              background: "transparent"
            }
          }} onClick={() => handleButtonClick("video")}>
            <img src={video_icon} alt="video"/>
            <p>Select a Video</p>
          </Button>
          <Button variant="contained" fullWidth sx={{
            background: "transparent",
            border: `2px solid ${colors.purple}`,
            minHeight: "3rem",
            fontSize: "16px",
            fontWeight: "500",
            borderRadius: "38px",
            marginTop: "24px",
            display: "flex",
            alighItems: "center",
            gap: "8px",
            "&:hover": {
              background: "transparent"
            }
          }} onClick={() => handleButtonClick("image")}>
            <img src={image_icon} alt=""/>
            <p>Select Image(s)</p>
          </Button>

          <StyledSpan>Create content from any link/URL. Copy and paste below to upload your Image or GIF.</StyledSpan>
          <TextField
            value={fileUrl}
            onChange={handleFileUrl}
            placeholder="URL"
            disabled={urlLoad}
            sx={{
              marginTop: "1rem",
              width: "100%",
              background: "transparent",
              "& label.Mui-focused": {
                color: "#D8DFEC"
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid rgba(216, 223, 236, 0.3)"
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                color: "white",
                border: "1px solid rgba(216, 223, 236, 0.3)",
                height: "50px"
              },
              "& .MuiFormHelperText-root.Mui-error": {
                margin: 0,
                marginTop: 5,
              },
              "& input.Mui-disabled": {
                color: "white",
                "-webkit-text-fill-color": "white"
              }
            }}
          />

          {urlLoad && (
            <>
              <LinearProgress
                sx={{
                  marginTop: "20px",
                  borderRadius: "5px",
                  ".MuiLinearProgress-bar": {
                    backgroundColor: "#8F2CF2"
                  }
                }}
                color="secondary"
              />
              <p className="upload-text">Please wait while we're downloading the video.</p>
            </>
          )}
          {!!urlError && <ErrorText>{urlError}</ErrorText>}
          <NavLink to="/guidelines">Community Guidelines</NavLink>
        </Container>
      </FileUploader>
      <input
        style={{ display: "none" }}
        ref={videoInputRef}
        accept={videoAcceptTypes.join(",")}
        type="file"
        onChange={(e) => handleFileChange(e, videoAcceptTypes)}
      />
      <input
        style={{ display: "none" }}
        ref={imageInputRef}
        multiple
        type="file"
        accept={imageAcceptTypes.join(",")}
        onChange={(e) => handleFileChange(e, imageAcceptTypes)}
      />
    </ContainerWrap>
  );
};

export default UploadSelectGifType;

const ContainerWrap = styled("div")({
  ".file-drop": {
    width: "100%",
    height: "100%",
    position: "absolute",
    left: "0",
    paddingTop: "200px",

    "&.is-disabled": {
      cursor: "auto",
      border: "none",
    },
    "input:disabled + .iBDCDI": {
      background: "transparent",
      display: "none"
    }
  }
});

const Container = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  maxWidth: "690px",
  margin: "auto",
  ".phone &, .tablet &": {
    padding: "1rem",
  },
  "& h4": {
    fontWeight: 500,
    fontSize: "16px"
  },
  "& a": {
    color: colors.redPink,
    textAlign: "center",
    marginTop: "2rem",
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "14px",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  ".upload-text": {
    fontSize: "14px",
    marginTop: "10px"
  },
});

const ErrorText = styled("p")({
  color: colors.redPink,
  textAlign: "center",
  margin: "1rem 0"
});

const StyledSpan = styled("span")({
  color: "white",
  opacity: 0.6,
  fontWeight: 500,
  fontSize: "14px",
  display: "block",
  marginTop: "40px",
  textAlign: "left"
});
